import React, { FC, useState, useEffect, HTMLAttributes } from 'react'
import {
  useDisclosure,
  Modal,
  ModalContent,
  ModalBody,
  Icon,
  Button,
  Box,
  Image,
  Heading,
  Text,
  ModalCloseButton,
  Spinner,
} from '@chakra-ui/core'
import { RemoveScroll } from 'react-remove-scroll'
import { isMobileOnly } from 'react-device-detect'
import { useRecoilValue, useRecoilState, useSetRecoilState, useRecoilCallback, useRecoilValueLoadable } from 'recoil'
import { Button as ButtonComponent } from '@engyalo/storefront-components'
import {
  removeProductFromCartCanceled,
  removeProductFromCartCompleted,
  resetCartCanceled,
  resetCartCompleted,
  resetCartStart,
  viewCart,
} from '@engyalo/storefront-events'
import { useQuery } from '@apollo/client'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import {
  IProduct,
  IConfig,
  IOrder,
  IPreloadedCart,
  ICartDictionary,
  ICartStats,
} from '@engyalo/storefront-types/lib/plugins/model'
import {
  IProductsPricedPerCollection,
  IProductsPricedPerCollectionCollection,
  IProductsPricedPerCollectionVariables,
} from '@engyalo/storefront-types/lib/graphql/product'
import { customCartVariables, customCart as ICustomCart } from '@engyalo/storefront-types/lib/graphql/cart'
import { configState, cartState, orderConfigState, orderState } from '../../recoil/atoms'
import {
  cartStatsState,
  hasFetchedPreloadedCustomizationsState,
  producstWithPreloadedCustomizationsState,
} from '../../recoil/selectors'
import { CartItem } from '../../components/CartItem'
import { LinkButton } from '../../lib/types'
import { customCart, getProductsByListOfIDs } from '../../queries'
import { getFlatProductValues } from '../../utils/analytics'
import { ILocationSearch, skipIfInPreviewMode } from '../../utils/mode'
import { getPreloadedCartWithCustomizations } from '../../utils/preloaded-cart'
import _ from 'lodash'
import { ButtonTotalCart } from '../../components/ButtonTotalCart'

export const Cart: FC = () => {
  const { t } = useTranslation()
  const location = useLocation<ILocationSearch>()
  const { customer, order } = useParams()
  const config = useRecoilValue<IConfig>(configState)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const [isCartVerified, setCartIsVerified] = useState(false)
  const [globalOrder, setGlobalOrder] = useRecoilState<IOrder>(orderState)
  const [minAmout, setMinAmout] = useState<number | undefined>(undefined)
  const [verticalHeight, setVerticalHeight] = useState(window.innerHeight * 0.01)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [productToDelete, setProductToDelete] = useState<IProduct | null>(null)
  const [cart, setCart] = useState<ICartDictionary>({})
  const setCartState = useSetRecoilState<ICartDictionary>(cartState)
  const [cartStats, setCartStatsState] = useState<ICartStats>({ totalNum: 0, totalPrice: 0 })
  const hasPreloadedCartWithCustomization = useRecoilValue(hasFetchedPreloadedCustomizationsState(cart))
  const preloadedProductsWithCustomizations = useRecoilValue(producstWithPreloadedCustomizationsState)

  const [isLoading, setIsLoading] = useState(true)

  const firstCartLoad = useRecoilCallback(({ snapshot }) => async () => {
    const atomCart = await snapshot.getPromise(cartState)
    const tempCart = { ...atomCart }
    setCart(tempCart)
    setCartState(tempCart)

    const atomCartStats = await snapshot.getPromise(cartStatsState)
    const tempCartStats =  { ...atomCartStats }
    setCartStatsState(tempCartStats)

    skipIfInPreviewMode(() => {
      const getCartItemValues = getFlatProductValues(tempCart)
      viewCart({
        cart: {
          size: cartStats.totalNum,
          value: cartStats.totalPrice,
          ids: getCartItemValues('id'),
          skus: getCartItemValues('sku'),
          names: getCartItemValues('name'),
          categories: getCartItemValues('category'),
          note: globalOrder.orderNote,
        },
        session,
      })
    })
  })

  useEffect(() => {
    firstCartLoad()
  }, [])

  useQuery<IProductsPricedPerCollection, IProductsPricedPerCollectionVariables>(getProductsByListOfIDs, {
    variables: {
      name: customer,
      configId: order,
      productIds: preloadedProductsWithCustomizations,
    },
    skip: hasPreloadedCartWithCustomization,
    onCompleted(data) {
      if(data) {
        setCart((oldCart) =>
          getPreloadedCartWithCustomizations(
            oldCart,
            data.productsPricedPerCollection as IProductsPricedPerCollectionCollection[],
            config.preloadedCart as IPreloadedCart[]
          )
        )
        setCartState((oldCart) =>
          getPreloadedCartWithCustomizations(
            oldCart,
            data.productsPricedPerCollection as IProductsPricedPerCollectionCollection[],
            config.preloadedCart as IPreloadedCart[]
          )
        )
      }
    },
  })

  const session = {
    userID: orderConfig.userID || '',
    customer: config.customer,
    sessionID: orderConfig.id || '',
  }

  useQuery<ICustomCart, customCartVariables>(customCart, {
    variables: {
      storefrontID: config.storefrontId,
      orderConfigID: orderConfig.id || '',
      /**
       * This is ugly but needed by now. Product query gets an
       * autogenerated `__typename` property which is useful for caching
       * but `customCart` query doesn't expect it, thus we need to remove
       * it from the cart items list, however TS complains because it
       * doesn't find a `__typename` property in the IProduct interface :(
       * TODO: Remove @ts-ignore by ensuring the right types in all places
       */
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      cart: Object.values(cart).map(({ __typename, ...product }) => product),
    },
    skip: !config.enabledCustomCart || isCartVerified,
    onCompleted(data: ICustomCart) {
      const verifiedCart = data.customCart.reduce(
        (prev, cartItem) => ({
          ...prev,
          [cartItem?.id || '']: cartItem,
        }),
        {}
      )
      setCartIsVerified(true)
      setCart(verifiedCart)
      setCartState(verifiedCart)
    },
  })

  const deleteCartAsync = useRecoilCallback(({snapshot}) => async () => {
    const atomCart = await snapshot.getPromise(cartState);
    const cart =  { ...atomCart }

    if (productToDelete) {
      skipIfInPreviewMode(() => {
        removeProductFromCartCompleted({
          product: {
            id: productToDelete.id || '',
            sku: productToDelete.sku || '',
            name: productToDelete.name || '',
            price: productToDelete.price || 0,
            totalPrice:
              productToDelete?.price && productToDelete?.quantity
                ? productToDelete.price * productToDelete.quantity
                : 0,
            category: productToDelete.category || '',
          },
          session,
        })
      })
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      deleteActionAsync(productToDelete)
    } else {
      skipIfInPreviewMode(() => {
        const getCartItemValues = getFlatProductValues(cart)

        resetCartCompleted({
          cart: {
            size: cartStats.totalNum,
            value: cartStats.totalPrice,
            ids: getCartItemValues('id'),
            skus: getCartItemValues('sku'),
            names: getCartItemValues('name'),
            categories: getCartItemValues('category'),
            note: globalOrder.orderNote,
          },
          session,
        })
      })
      setCart({})
      setCartState({})
    }
    onClose()
  })

  const deleteActionAsync = useRecoilCallback(({ snapshot }) => async (product: IProduct) => {
    const atomCart = await snapshot.getPromise(cartState)
    const tempCart = { ...atomCart }
    if (product && product.cartId) {
      const currentSku = product.cartId
      if (tempCart[currentSku]) {
        delete tempCart[currentSku]
      }
    }
    setCart(tempCart)
    setCartState(tempCart)
    setProductToDelete(null)
  })

  const deleteItem = (product: IProduct): void => {
    setProductToDelete(product)
    onOpen()
  }

  useEffect(() => {
    function handleResize() {
      const newVerticalHeight = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${newVerticalHeight}px`)
      setVerticalHeight(newVerticalHeight)
    }

    window.addEventListener('resize', handleResize)
  })

  const getMinHeight = () => {
    const screenHeight = window.innerHeight
    const headerHeight = 78
    const footerButton = 18 + 24
    const footerMinOrder = 24
    const footerOrderNote = 102
    const footerPadding = 24

    if (config) {
      if (isMobileOnly) {
        if (cartStats.totalNum > 0) {
          if (config.enabledOrderNote) {
            if (config && config.minAmount && config.minAmount > 0) {
              const minHeight = screenHeight - footerPadding - footerButton - footerMinOrder - footerOrderNote
              return minHeight
            } else {
              const minHeight = screenHeight - footerPadding - footerButton - footerOrderNote
              return minHeight
            }
          } else {
            if (config && config.minAmount && config.minAmount > 0) {
              const minHeight = screenHeight - footerPadding - footerButton - footerMinOrder
              return minHeight
            } else {
              const minHeight = screenHeight - footerPadding - footerButton
              return minHeight
            }
          }
        } else {
          if (config && config.minAmount && config.minAmount > 0) {
            const minHeight = screenHeight - footerPadding - footerButton - footerMinOrder
            return minHeight
          } else {
            const minHeight = screenHeight - footerPadding - footerButton
            return minHeight
          }
        }
      }
    }

    return ''
  }

  useEffect(() => {
    const { metadata } = orderConfig

    if (metadata) {
      try {
        const jsonMetadata = JSON.parse(metadata)

        const { client } = jsonMetadata
        if (client) {
          const { minAmount } = client
          setMinAmout(Number.parseFloat(minAmount))
        }
      } catch {
        setMinAmout(config.minAmount)
      }
    } else {
      setMinAmout(config.minAmount)
    }
  }, [config, orderConfig])

  useEffect(() => {
    setIsLoading(false)
  }, [cart])

  if (isLoading) {
    return (
      <Spinner
        color="orange.500"
        thickness="4px"
        speed="0.65s"
        emptyColor="gray.200"
        size="xl"
        justifyContent={'center'}
        alignItems={'center'}
      />
    )
  }

  return (
    <Box h="100%" maxW="100vh" w="100%" d="flex" flexDir="column">
      {isOpen && (
        <Box position="absolute" opacity={0.5} backgroundColor="black" width="100%" height="100%" zIndex={50} />
      )}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalContent>
          <RemoveScroll>
            <ModalCloseButton
              background="transparent"
              borderStyle="none"
              borderColor="unset"
              onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                event.preventDefault()
                if (productToDelete) {
                  skipIfInPreviewMode(() => {
                    removeProductFromCartCanceled({
                      product: {
                        id: productToDelete.id || '',
                        sku: productToDelete.sku || '',
                        name: productToDelete.name || '',
                        price: productToDelete.price || 0,
                        totalPrice:
                          productToDelete.price && productToDelete.quantity
                            ? productToDelete.price * productToDelete.quantity
                            : 0,
                        category: productToDelete.category || '',
                      },
                      session,
                    })
                  })
                } else {
                  const getCartItemValues = getFlatProductValues(cart)
                  skipIfInPreviewMode(() => {
                    resetCartCanceled({
                      cart: {
                        size: cartStats.totalNum,
                        value: cartStats.totalPrice,
                        ids: getCartItemValues('id'),
                        skus: getCartItemValues('sku'),
                        names: getCartItemValues('name'),
                        categories: getCartItemValues('category'),
                        note: globalOrder.orderNote,
                      },
                      session,
                    })
                  })
                }

                onClose()
              }}
            />
            <ModalBody>
              <Heading as="h3" fontSize="md" textAlign="center" paddingTop="4rem" paddingBottom="1.6rem">
                {productToDelete
                  ? t('cart-removeQuestion', { product: productToDelete.name })
                  : t('cart-removeAllQuestion')}
                <span role="img" aria-label="scream">
                  😱
                </span>
              </Heading>
              <Box paddingBottom="2rem">
                <ButtonComponent onClick={deleteCartAsync} danger>
                  {productToDelete ? t('cart-remove') : t('cart-removeAll')}
                </ButtonComponent>
              </Box>
            </ModalBody>
          </RemoveScroll>
        </ModalContent>
      </Modal>
      <Box
        position="sticky"
        background="white"
        flex="0 0 auto"
        d="flex"
        top={0}
        zIndex={2}
        width="100%"
        maxW="42.5rem"
        flexDir="column"
      >
        <Box h="4rem" d="flex" width="100%" maxW="42.5rem">
          <LinkButton
            to={{ pathname: `/${customer}/${order}`, search: location.search }}
            h="100%"
            w="3.2rem"
            background="transparent"
            borderColor="transparent"
            size="lg"
            aria-label="cart"
            icon="chevron-left"
            color="primary.600"
          />
          <Image alt="logo" h="3.6rem" alignSelf="center" margin="auto" src={config.logo} />
        </Box>
        <Box
          padding="1.2rem 1.2rem 0.8rem 1.6rem"
          borderBottom="0.1rem solid"
          borderColor="gray.200"
          position="sticky"
          top={0}
          flex="0 0 auto"
          zIndex={2}
          background="white"
          width="100%"
          maxW="42.5rem"
        >
          <Heading as="h2" fontSize="cartTitle" fontWeight="normal">
            {t('cart-title')}
          </Heading>
          {cartStats.totalNum > 0 && (
            <Box display="flex" justifyContent="space-between">
              <Text fontSize="sm" color="gray.100">
                {t('cart-resume', { count: cartStats.totalNum })}
              </Text>
              <Button
                color="primary.500"
                variant="link"
                border="transparent"
                fontSize="sm"
                textDecor="underline"
                backgroundColor="transparent"
                fontWeight="normal"
                onClick={(): void => {
                  setProductToDelete(null)
                  skipIfInPreviewMode(() => {
                    const getCartItemValues = getFlatProductValues(cart)
                    resetCartStart({
                      cart: {
                        size: cartStats.totalNum,
                        value: cartStats.totalPrice,
                        ids: getCartItemValues('id'),
                        skus: getCartItemValues('sku'),
                        names: getCartItemValues('name'),
                        categories: getCartItemValues('category'),
                        note: globalOrder.orderNote,
                      },
                      session,
                    })
                  })
                  onOpen()
                }}
              >
                {t('cart-cleanCart')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      {/* Body */}
      {cartStats.totalNum > 0 ? (
        <Box flex="1" overflowY="auto">
          {Object.keys(cart).map((key) => (
            <CartItem deleteItem={deleteItem} key={key} product={cart[key]} />
          ))}
        </Box>
      ) : (
        <Box
          flex="1"
          display="flex"
          flexDir="column"
          justifyContent="center"
          alignItems="center"
          h="100%"
          minH={`${getMinHeight()}px`}
          overflowY="scroll"
        >
          <Icon name="settings" size="4.8rem" paddingBottom="0.8rem" />
          <Text fontSize="md" textAlign="center" whiteSpace="pre-line">
            {t('cart-emptyMessage')}
          </Text>
          <Link to={{ pathname: `/${customer}/${order}`, search: location.search }}>
            <Text
              color="primary.500"
              aria-label="backToCart"
              border="transparent"
              fontSize="sm"
              textDecor="underline"
              backgroundColor="transparent"
              fontWeight="normal"
              paddingTop="32px"
            >
              {t('cart-backCatalogue')}
            </Text>
          </Link>
        </Box>
      )}
      {/* Body */}
      {/* Footer */}
        <ButtonTotalCart />
      {/* Footer */}
    </Box>
  )
}
